import React, { useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '@/firebase';
import { trackRedditEvent } from '@/utils/redditPixel';
import { trackMetaStandardEvent } from '@/utils/metaPixel';
import { trackGtagEvent } from '@/utils/Analytics';
import { isInternalTraffic } from '@/utils/internalTraffic';
import { getLeadAttribution } from '@/utils/emailAttribution';
import { callWithTimeout } from '@/utils/callWithTimeout';
import { computeLeadId, saveLeadRef } from '@/utils/leadRef';
import { redeemPassPlanAuth, type PassPlanRedeemResult } from '@/utils/passPlanAuth';
import { logger } from '@/utils/logger';
import type { CaptureLeadRequest } from '@/types/backendCallables';
import type { DiagnosticVariant } from '@/utils/diagnosticVariant';
import { appHref } from '@/utils/appHref';
import { savePassPlanToken } from '@/utils/passPlanAccess';
import { isPassPlanTrialAtMintEnabled } from '@/config/featureFlags';
import { AgeConfirm, AGE_REQUIRED_MESSAGE } from '@/shared/components/AgeConfirm';

// App Check reCAPTCHA can HANG the captureLead callable indefinitely in
// privacy/adblock/in-app browsers, leaving the "Unlocking…" spinner up forever
// so the held score never reveals and the lead is lost. Bound every invocation
// with a hard timeout (mirrors TrialSignupFlow's createTrialUser defense).
// `retries: 0` — captureLead is a mutation (stores the lead + emails the Pam
// results), so a timed-out call may have run server-side; auto-retrying risks a
// duplicate lead/email. On timeout we reject → the catch surfaces a retryable
// error and the user can resubmit by hand.
const CAPTURE_TIMEOUT_MS = 15000;

const GIFT_CHIPS = ['New exam format', 'FIRST / NEXT / BEST', '8-week plan', 'Ethics traps'];

const PassPlanGiftCard: React.FC = () => (
  <div className="mb-4 flex items-center gap-3.5 rounded-[14px] border-2 border-[#E8DFD1] bg-[#FAF7F2] p-3.5 shadow-[0_4px_0_rgb(var(--color-ledge))]">
    <div aria-hidden="true"
      className="relative h-[98px] w-[76px] shrink-0 rounded-l rounded-r-lg bg-primary-500 pl-3 pr-2 pt-2.5 font-display text-[11px] font-bold leading-tight text-white shadow-[inset_6px_0_0_rgba(0,0,0,0.18),0_3px_0_rgb(var(--color-primary-700))]">
      <span className="absolute right-2.5 -top-[3px] h-[22px] w-3 bg-celebrate-deep [clip-path:polygon(0_0,100%_0,100%_100%,50%_78%,0_100%)]" />
      <span className="mb-1 block whitespace-nowrap pr-4 font-sans text-[6px] tracking-[0.08em] opacity-75">2026 ASWB</span>
      The Pass Plan
    </div>
    <div className="min-w-0">
      <span className="mb-1 inline-block rounded-full bg-celebrate-100 px-2 py-0.5 text-[10px] font-extrabold uppercase tracking-[0.12em] text-celebrate-700">Your gift</span>
      <p className="mb-1.5 font-display text-base font-bold leading-snug text-neutral-900">15 pages, built around your gaps</p>
      <ul className="flex flex-wrap gap-1">
        {GIFT_CHIPS.map((c) => (
          <li key={c} className="rounded-lg border border-[#E8DFD1] bg-white px-1.5 py-0.5 text-[11px] font-bold text-primary-500">{c}</li>
        ))}
      </ul>
    </div>
  </div>
);

/**
 * The email gate for the Pass Plan / diagnostic reveal. It is the funnel's
 * narrowest point, so it lives in ONE place and is reused by the diagnostic's
 * held-reveal (TryAQuestion) and the standalone /pass-plan unlock.
 *
 * On submit it calls `captureLead`, which stores the lead, emails the results as
 * Pam, AND returns a signed Pass Plan download token. That token flows back
 * through `onCaptured` so the caller can reveal the score and unlock the packet
 * without a second email — and, in the background, is redeemed for a verified
 * Firebase user (see `utils/passPlanAuth`) so the warmest addresses on the site
 * stop being leads we can't mail as people.
 */
const PassPlanCapture: React.FC<{
  lead: { score: number; total: number; readiness: number; weakAreas: string[]; examType: string };
  weakest?: string | null;
  className?: string;
  /**
   * The page's tracker, which carries attribution AND drops internal traffic.
   * Omitted on pages without one (e.g. /pass-plan), where an internal-aware GA4
   * event is fired directly instead.
   */
  track?: (name: string, extra?: Record<string, unknown>) => void;
  /**
   * Fired once captureLead resolves — the moment that unlocks the held reveal.
   * Receives the signed download token (undefined when the backend minted none).
   */
  onCaptured?: (downloadToken?: string) => void;
  /**
   * Which surface this capture ran on, so the redeemed account is labelled by
   * where it actually happened instead of always as the diagnostic. Required —
   * this component is shared by TryAQuestion (`try_capture`) and the standalone
   * /pass-plan page (`pass_plan_page`); there is no safe default.
   */
  source: 'try_capture' | 'pass_plan_page';
  /** Fired when the background redeem settles; `signed-in` means they now have an account. */
  onRedeemed?: (result: PassPlanRedeemResult) => void;
  /** Variant B of the try_gate_copy_short test: the short label + the Pass Plan gift card. */
  shortCopy?: boolean;
  /** The homepage A/B arm this capture belongs to, forwarded to the redeem. */
  diagnosticVariant?: DiagnosticVariant;
}> = ({ lead, weakest, className = '', track, onCaptured, onRedeemed, source, shortCopy = false, diagnosticVariant }) => {
  const [email, setEmail] = useState('');
  const [state, setState] = useState<'idle' | 'sending' | 'sent' | 'error'>('idle');
  const [ageConfirmed, setAgeConfirmed] = useState(false);
  const [ageError, setAgeError] = useState(false);
  const trialAtMint = isPassPlanTrialAtMintEnabled();

  // Use the page tracker when given (it merges attribution); otherwise fire a
  // plain, internal-traffic-aware GA4 event so the lead is still counted.
  const fire = track ?? ((name: string, extra?: Record<string, unknown>) => {
    if (!isInternalTraffic()) trackGtagEvent(name, extra ?? {});
  });

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (state === 'sending') return;
    if (!ageConfirmed) {
      setAgeError(true);
      return;
    }
    setState('sending');
    try {
      // Attribute the lead to the channel that ORIGINALLY brought them (stored
      // 90-day acquisition record), not the last internal click. Undefined when
      // there's no channel signal — spreads to nothing, leaving the payload intact.
      const attribution = getLeadAttribution();
      const payload: CaptureLeadRequest = { email, ...lead, ...attribution };
      const res = await callWithTimeout(
        () => httpsCallable(functions, 'captureLead')(payload),
        { timeoutMs: CAPTURE_TIMEOUT_MS, retries: 0 },
      );
      const downloadToken = (res?.data as { downloadToken?: string } | undefined)?.downloadToken;
      if (downloadToken) savePassPlanToken(downloadToken);
      // Turn the lead into a VERIFIED user while they read their results. The
      // token is proof of the mailbox, so this is the password-path equivalent
      // of what Google/Apple already give us. Deliberately NOT awaited: the
      // reveal below is what they traded their email for and must never wait on
      // (or be lost to) an account mint. `redeemPassPlanAuth` never throws.
      void redeemPassPlanAuth(downloadToken, source, diagnosticVariant).then((r) => onRedeemed?.(r));
      // Compute the canonical email hash ONCE. It is: the lead-ref stash value
      // (so the account-last /onboarding flow can mirror pre-account personalize
      // answers into this lead doc, addressed by hash, without ever storing the
      // email), the Meta advanced-matching `em`, AND the stem of the Lead dedup
      // event_id ("lead-" + hash). Because computeLeadId === sha256(lower(trim
      // (email))), `em` and the backend CAPI's event_id derive from this exact
      // value. Best-effort — a hash failure must never affect capture, so we
      // fall back to a Lead with no em/eventID.
      let leadHash: string | undefined;
      try {
        leadHash = await computeLeadId(email);
        saveLeadRef(leadHash);
      } catch (err) {
        logger.warn('PassPlanCapture', 'lead-ref stash failed (non-fatal)', { err });
      }
      fire('diagnostic_email_captured');
      // Ad-platform Lead conversions (both no-op for internal traffic / when the
      // pixel isn't configured). Meta fires the STANDARD Lead event so its
      // optimizer counts it as a conversion, WITH advanced matching (em) and the
      // event_id so it dedups against the backend Lead CAPI. Google Ads' lead
      // signal is the GA4 `diagnostic_email_captured` event above, marked as a
      // conversion in GA4.
      trackRedditEvent('Lead');
      if (leadHash) {
        trackMetaStandardEvent('Lead', { em: leadHash }, `lead-${leadHash}`);
      } else {
        trackMetaStandardEvent('Lead');
      }
      setState('sent');
      // Unlock the reveal / download. The parent typically swaps this card out on
      // this call, so the 'sent' state below is a fallback for standalone use.
      onCaptured?.(downloadToken);
    } catch {
      setState('error');
    }
  };

  return (
    <div className={className}>
      {state === 'sent' ? (
        <p className="text-center text-emerald-700 font-semibold py-1" data-clarity-mask="true">
          ✓ Unlocked. The Pass Plan is in your inbox{weakest ? <> (focused on {weakest})</> : ''}.{' '}
          <a href={appHref('/pass-plan?src=diagnostic_sent')} className="underline text-emerald-800 hover:text-emerald-900">Or grab it now →</a>
        </p>
      ) : (
        <>
          {shortCopy ? (
            <>
              <p className="text-sm font-semibold text-neutral-900 mb-2">📩 Enter your email to see your results</p>
              <PassPlanGiftCard />
            </>
          ) : (
            <>
              <p className="text-sm font-semibold text-neutral-900 mb-1">📩 Enter your email to unlock your results</p>
              <p className="text-sm text-neutral-600 mb-3">I&apos;ll show you exactly where you stand and send your 15-page Pass Plan: the 2026 format explained, the FIRST/NEXT/BEST method with worked questions, an 8-week plan built around gaps like yours, the Ethics traps, and an honest readiness bar. Reviewed by Pam Coin, LCSW. {trialAtMint ? 'Your email starts your free week' : 'Your email, no account'}, unsubscribe anytime.</p>
            </>
          )}
          <div className="mb-2">
            <AgeConfirm
              checked={ageConfirmed}
              onChange={(checked) => {
                setAgeConfirmed(checked);
                if (checked) setAgeError(false);
              }}
            />
          </div>
          <form onSubmit={submit} className="flex flex-col sm:flex-row gap-2">
            <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@email.com" autoComplete="email"
              aria-label="Your email address, to reveal your results and send the Pass Plan"
              className="flex-1 px-4 py-3 rounded-xl border-2 border-neutral-200 focus:border-primary-400 focus:outline-none text-neutral-800" />
            <button type="submit" disabled={state === 'sending'}
              className="px-5 py-3 bg-primary-600 hover:bg-primary-700 text-white rounded-xl font-bold transition-colors disabled:opacity-50">
              {state === 'sending' ? 'Unlocking…' : 'Reveal my results'}
            </button>
          </form>
          {shortCopy && <p className="text-xs text-neutral-500 text-center mt-2">Reviewed by Pam Coin, LCSW · {trialAtMint ? 'Starts your free week' : 'No account'} · Unsubscribe anytime</p>}
          {ageError && <p role="alert" className="text-sm text-red-600 mt-2">{AGE_REQUIRED_MESSAGE}</p>}
          {state === 'error' && <p className="text-sm text-red-600 mt-2">Hmm, that didn&apos;t go through. Please try again.</p>}
        </>
      )}
    </div>
  );
};

export default PassPlanCapture;
